import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener,
         Inject, OnDestroy, Output, Renderer2, ViewChild
       } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { applyContentImageSwapping, GallerySource
       } from 'app/collage-designer/stage/drag-drop';
import { PlaceholderService
       } from 'app/collage-designer/stage/placeholder/placeholder.service';
import { DeleteGalleryImageDialogComponent
       } from 'app/dialogs/delete-gallery-image-dialog/delete-gallery-image-dialog.component'; // eslint-disable-line
import { Button } from 'app/dialogs/dialog-buttons.component';
import { GalleryConstants } from 'core/constants';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber
       } from 'core/utils/async-event-emitter';
import { Subscription } from 'rxjs';
import { GalleryImage, GalleryService } from './gallery.service';

@Component({
    selector: 'gallery',
    templateUrl: './gallery.component.html',
    styleUrls: ['./gallery.component.scss'],
    standalone: false
})
export class GalleryComponent implements OnDestroy
{
  private initAllPhotosArePlacedCheck: boolean = false;
  private galleryService: GalleryService;
  private placeholderService: PlaceholderService;
  private renderer: Renderer2;
  private subscriptions: Subscription = new Subscription();
  private newImageAvailableSubscription:
      AsyncEventEmitterSubscriber<GalleryImage>;
  private uploadProgressChangedSubscription:
      AsyncEventEmitterSubscriber<boolean>;
  private dragDropInitializedImages: WeakSet<HTMLImageElement> = new WeakSet();
  private thumbnailResizeObserver?: ResizeObserver;
  private currentThumbnailShortSide?: number;

  public galleryImages: Array<GalleryImage> = [];
  public imagesCount: number = 0;
  public remainingImageUploads: number = 0;
  public uploadProgress: boolean = false;
  public showUsedImages: boolean = false;
  public checkmarkUrl: string =
    Utils.url('./assets/img/checkmark.svg');
  public noUploadedPhotosUrl: string =
    Utils.url('./assets/img/msg-no-uploaded-photos.svg');
  public allPhotosArePlacedUrl: string =
    Utils.url('./assets/img/msg-all-images-placed.svg');

  @Output() public imageClicked: EventEmitter<string> =
      new EventEmitter<string>();
  @Output() public imageDeleted: EventEmitter<string> =
      new EventEmitter<string>();
  @ViewChild('imageSelect') public imageSelect: any;
  @ViewChild('thumbnailsWrapper') public thumbnailsWrapper: any;

  public constructor(
      private dialog: MatDialog,
      private changeDetector: ChangeDetectorRef,
      @Inject(Renderer2) renderer: Renderer2,
      @Inject(GalleryService) galleryService: GalleryService,
      @Inject(PlaceholderService) placeholderService: PlaceholderService,
      @Inject(EventTrackingService) private eventTrackingService,
      @Inject(ProductService) private productService)
  {
    this.renderer = renderer;
    this.galleryService = galleryService;
    this.placeholderService = placeholderService;
    this.imagesCount = this.galleryService.getImages().length;
    this.remainingImageUploads = this.galleryService.imagesToUpload;
    this.uploadProgress = this.galleryService.getUploadInProgress();

    this.subscriptions.add(
        this.galleryService.imageStockChanged.subscribe(() => {
            this.imagesCount = this.galleryService.getImages().length;
            this.filterGalleryImages();
        }));

    this.subscriptions.add(
        this.galleryService.usedImageChanged.subscribe(() => {
            this.filterGalleryImages();
        }));

    this.uploadProgressChangedSubscription =
        this.galleryService.uploadProgressChanged.subscribe(async (value) => {
            this.uploadProgress = value;
            this.remainingImageUploads = this.galleryService.imagesToUpload;

            if (this.remainingImageUploads === 0 &&
                !this.initAllPhotosArePlacedCheck) {
              this.initAllPhotosArePlacedCheck = true;
              this.showUsedImages =
                  this.galleryImages.every(img =>
                    this.galleryService.isImageUsed(img.id));
              this.filterGalleryImages();
            }
        });

    this.newImageAvailableSubscription =
      this.galleryService.newImageAvailable.subscribe(
        async (galleryImage: GalleryImage) => {
          this.galleryImages = [...this.galleryImages, galleryImage];
          this.remainingImageUploads =
            this.galleryService.imagesToUpload -
            this.galleryService.imagesUploaded;
        });
  }

  public imageIsUsed(imageId: string): boolean
  {
    return this.galleryService.isImageUsed(imageId);
  }

  public imageIsClicked(imageId: string): void
  {
    if (this.imageIsUsed(imageId) && Utils.isLargeDevice()) {
      return;
    }

    this.imageClicked.emit(imageId);
  }

  public imageIsSelected(imageId: string): boolean
  {
    return this.galleryService.isImageSelected(imageId);
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
    this.thumbnailResizeObserver?.disconnect();
    this.galleryService.newImageAvailable.unsubscribe(
      this.newImageAvailableSubscription);
    this.galleryService.uploadProgressChanged.unsubscribe(
      this.uploadProgressChangedSubscription);
  }

  public getThumbnailUrl(info: GalleryImage): string | undefined
  {
    if (!this.currentThumbnailShortSide) {
      return undefined;
    }

    const limit = encodeURIComponent(
      JSON.stringify({ short_side: this.currentThumbnailShortSide }));
    return Utils.url(`${info.url}?limit=${limit}&convert=image/jpeg`);
  }

  public removeImage(imageId: string): void
  {
    if (this.galleryService.isImageUsed(imageId)) {
          this.dialog.open(DeleteGalleryImageDialogComponent, {
            data: {},
            panelClass: 'dialog-pane',
            disableClose: true,
          }).afterClosed().subscribe(result => {
            if (result === Button.Choice.CONTINUE) {
              this.imageDeleted.emit(imageId);
              this.galleryService.removeImage(imageId);
            }
          });
    }
    else {
      this.galleryService.removeImage(imageId);
    }
  }

  public onImageLoad(image: GalleryImage): void
  {
    image.isLoaded = true;
  }

  private filterGalleryImages(): void
  {
    if (this.showUsedImages) {
      this.galleryImages = [...this.galleryService.getImages()];
    }
    else {
      this.galleryImages = [...this.galleryService.getImages(true)];
    }
  }

  public toggleUsedImages(): void
  {
    this.showUsedImages = !this.showUsedImages;
    this.filterGalleryImages();
  }

  private trackUploadPhotos(): void
  {
    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'init_photo_upload',
      label: 'gallery_upload_button',
    });
  }

  private removeSelectionAndHighlighter(): void
  {
    this.galleryService.setSelectedImageId(undefined);
    const placeholders = this.placeholderService.getImagePlaceholders(true);
    for (let placeholder of placeholders) {
      placeholder.hidePlaceholderHighlighter();
    }
  }

  public onUploadClick(
      input: HTMLInputElement, removeSelection: boolean = false): void
  {
    input.click();
    this.trackUploadPhotos();

    if (removeSelection) {
      this.removeSelectionAndHighlighter();
    }
  }

  public onLoad(image: GalleryImage, element: HTMLImageElement): void
  {
    image.isLoaded = true;

    if (this.dragDropInitializedImages.has(element)) {
      return;
    }
    this.dragDropInitializedImages.add(element);

    applyContentImageSwapping(
        new GallerySource(
            element, this.galleryService, this.placeholderService));
  }

  @ViewChild('imgElement') set thumbnailElement(el: ElementRef | undefined)
  {
    if (!!this.thumbnailResizeObserver) {
      this.thumbnailResizeObserver.disconnect();
      this.thumbnailResizeObserver = undefined;
    }

    if (!!el) {
      const obs = new ResizeObserver((_) => {
        this.updateThumbnailBoxSize(Math.max(
          el.nativeElement.offsetWidth,
          el.nativeElement.offsetHeight
        ));
      });

      obs.observe(el.nativeElement);
      this.thumbnailResizeObserver = obs;
    }
  }

  private updateThumbnailBoxSize(longSide: number): void
  {
    if (longSide === 0) {
      return;
    }

    // NOTE: The image is shown with `object-fit: cover`, so its short side
    // has to be at least the box's long side to never be upscaled.
    const shortSide = Utils.steppedLimit(longSide);

    if (shortSide === this.currentThumbnailShortSide) {
      return;
    }

    this.currentThumbnailShortSide = shortSide;
    this.changeDetector.detectChanges();
  }
}
